import * as React from 'react';
import styled from 'styled-components';
import {transparentize} from 'polished';
import {getImageByPreview} from 'api/library';
import RoundCheckbox from 'components/RoundCheckbox';
import {InstanceObject} from '..';
import {transition} from 'helpers/css';

export type InstanceProps<T extends InstanceObject> = T & {
  selected: boolean;
  onSelect(selected: boolean): void;
}

const Container = styled.label`
  padding: 4px;
  margin: 5px 0;
  cursor: pointer;
  column-gap: 14px;
  align-items: flex-start;
  grid-template-columns: minmax(160px, min-content) 1fr min-content;
  display: grid;
  
  border-radius: 5px;
  ${transition()};
  
  &:hover,
  &:focus-visible {
    background: ${({theme}) => `
      ${transparentize(.98, theme.colors.white)};
    `};
  }

  &:active{
    background: ${({theme}) => `
      ${transparentize(.96, theme.colors.white)};
    `};
  }
`;

const Preview = styled.img`
  width: 100%;
  height: 90px;
  border-radius: 5px;
  object-fit: cover;
`;

const Title = styled.span`
  max-width: 280px;
  margin-top: 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  user-select: none;
`;

const Control = styled.div`
  margin: 6px;
`;

/**
 * This component renders the instance card.
 * @param {InstanceProps<T>} props
 * @return {React.ReactElement<InstanceProps<T>>}
 * @constructor
 */
export default function Instance<T extends InstanceObject>(
    props: InstanceProps<T>,
): React.ReactElement<InstanceProps<T>> {
  /**
   * This is the function of the render preview instance.
   * @return {JSX.Element}
   */
  function renderPreview(): JSX.Element {
    return (
      <Preview src={getImageByPreview(props.preview)} />
    );
  }

  /**
   * This is the function of the render title instance.
   * @return {JSX.Element}
   */
  function renderTitle(): JSX.Element {
    return (
      <Title>
        {props.title}
      </Title>
    );
  }

  /**
   * This is the function of the render control instance.
   * @return {JSX.Element}
   */
  function renderControl(): JSX.Element {
    /**
     * The change event handler.
     * @param {React.ChangeEvent<HTMLInputElement>} event
     */
    function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
      props.onSelect && props.onSelect(event.target.checked);
    }

    return (
      <Control>
        <RoundCheckbox
          checked={props.selected}
          onChange={handleChange}
        />
      </Control>
    );
  }

  return (
    <Container>
      {renderPreview()}
      {renderTitle()}
      {renderControl()}
    </Container>
  );
}
